'use client';

import { useMemo } from 'react';
import { Plus } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Button } from '@/shared/ui/shadcn/button';
import { useOptionalDesktopLayout } from '@/features/project/context/DesktopLayoutContext';
import { ConceptAngleType } from '@/shared/constants/concept-angle-type';
import type { ConceptAngle } from '@/types/concept';

interface ConceptContentTabsProps {
  conceptAngles: ConceptAngle[];
  onAddAngle?: () => void;
  /**
   * Pin the row to the top of the scroll while content scrolls underneath.
   * True in the two-column (desktop) content column. False on phone /
   * single-column, where the switcher scrolls away with the angle/render
   * content (Apple/Android parity — the switcher is page flow, not chrome).
   */
  pinned?: boolean;
}

/**
 * Top-of-content navigation: angle tabs (Main angle / #2 / #3 / +).
 *
 * Lives at the top of the leading (content) column in two-column mode.
 * The global Design/Blueprint picker now lives above both columns (see
 * `ProjectDesktopLayout`) — only angle navigation remains here, which
 * is content-scoped.
 *
 * Apple HIG split-view convention: navigation lives with the content,
 * inspector ([UnifiedRenderPanel variant="inline"]) holds only tools.
 */
export function ConceptContentTabs({
  conceptAngles,
  onAddAngle,
  pinned = true,
}: ConceptContentTabsProps) {
  const tAngles = useTranslations('angles');
  const tAngle = useTranslations('angle.menu');
  const tAngleOrdinal = useTranslations('angle.ordinal');
  const layoutContext = useOptionalDesktopLayout();

  const topTab = layoutContext?.activeTab ?? 'design';
  const selectedAngleId = layoutContext?.selectedAngleId;
  const setSelectedAngleId =
    layoutContext?.setSelectedAngleId ?? (() => {});

  const angleTabs = useMemo(() => {
    const sorted = [...conceptAngles].sort((a, b) => {
      const aMain = a.attributes.type === ConceptAngleType.MAIN;
      const bMain = b.attributes.type === ConceptAngleType.MAIN;
      if (aMain && !bMain) return -1;
      if (!aMain && bMain) return 1;
      return a.attributes.order - b.attributes.order;
    });
    // Derivative ordinals: 2nd, 3rd, …, 7th. Max plan caps angles at 7.
    const ordinalKeys: Record<number, string> = {
      2: 'second',
      3: 'third',
      4: 'fourth',
      5: 'fifth',
      6: 'sixth',
      7: 'seventh',
    };
    let derivativeIndex = 1;
    return sorted.map((ca) => {
      // The custom view name (set via the ⋯ → "Rename view" action) is the tab
      // label when present; otherwise fall back to the generic role label
      // (Main view / Second view / …). This replaces the legacy in-image name
      // pill — the tab IS the name now (cross-platform parity).
      const customName = ca.relationships?.angle?.meta?.name?.trim();
      // A CONSTRUCTED view has no photo to recognise it by, so "Main view" /
      // "Second view" would leave the user unable to tell the tabs apart. The
      // side it looks at is the identity there — use the server-localized
      // `viewpoint_label` ("Street facade") as the name whenever the user has
      // not renamed the view. Uploads have no viewpoint and keep the ordinal.
      const viewpointLabel =
        ca.relationships?.angle?.meta?.viewpoint_label?.trim();
      const isMain = ca.attributes.type === ConceptAngleType.MAIN;
      if (isMain) {
        return {
          id: ca.id,
          name: customName || viewpointLabel || tAngle('mainAngle'),
          isMain: true,
        };
      }
      derivativeIndex += 1;
      const key = ordinalKeys[derivativeIndex];
      const fallback = key ? tAngleOrdinal(key) : `#${derivativeIndex}`;
      return {
        id: ca.id,
        name: customName || viewpointLabel || fallback,
        isMain: false,
      };
    });
  }, [conceptAngles, tAngle, tAngleOrdinal]);

  const activeId =
    selectedAngleId || angleTabs.find((t) => t.isMain)?.id;

  if (topTab !== 'design' || angleTabs.length === 0) {
    return null;
  }

  return (
    // Angle tabs flush with the left edge of the content column.
    // Design/Blueprint segmented picker lives above both columns now,
    // so this row holds angle navigation only. In two-column (desktop) it's
    // pinned to the top of the content column so it stays visible while the
    // angle/render content scrolls underneath; on phone (single column) it
    // scrolls away with the content (`pinned={false}`, Apple/Android parity).
    // Material 3 Primary-tab styling (cross-platform parity: same shape as
    // Android PrimaryScrollableTabRow and Apple AngleTabsView).
    // - Active indicator: 3px rounded-top pill in brand teal hugging text width.
    // - Bottom divider: 1px subtle border across the full row width.
    // - + button follows the last tab with no extra gap (overflow-x scrolls).
    <div
      className={
        (pinned ? 'sticky top-0 z-10 ' : '') +
        '-mx-px flex w-full items-stretch overflow-x-auto border-b border-border/40 bg-panel'
      }
    >
      {angleTabs.map((tab) => {
        const isActive = tab.id === activeId;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() =>
              setSelectedAngleId(tab.isMain ? undefined : tab.id)
            }
            className={
              'relative whitespace-nowrap px-4 pt-3 pb-[6px] text-form-label transition-colors ' +
              (isActive
                ? 'text-primary font-semibold after:absolute after:left-3 after:right-3 after:bottom-0 after:h-[3px] after:bg-primary after:rounded-t-[3px]'
                : 'text-muted-foreground hover:text-foreground')
            }
          >
            {tab.name}
          </button>
        );
      })}
      {onAddAngle && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onAddAngle}
          aria-label={tAngles('addAngle')}
          // Align the icon with the tab text centre: tabs use asymmetric
          // pt-3/pb-[6px], so the text centre sits ~22px from the top
          // (12 + 19.6/2) while the row centre is ~19px. mt-1.5 puts the
          // 32px button's centre at 6+16=22px. Plain margin (not translate:
          // transforms grow the scrollable overflow of this overflow-x-auto
          // row and a vertical scrollbar appears).
          className="flex-shrink-0 self-start mt-1.5 h-8 w-8"
        >
          <Plus className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
